import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { effect } from '@preact/signals-core';
import { itemCount, isCartLoading } from '../store/cart-store';

/**
 * Cart Count Component
 *
 * A simple reactive component that displays the current cart item count.
 * Automatically updates when the cart changes.
 *
 * Usage:
 * <cart-count></cart-count>
 */
@customElement('cart-count')
export class CartCount extends LitElement {
  // Local state that gets updated by the signal effect
  @state() private count = 0;
  @state() private loading = false;

  // Store the effect cleanup function
  private _disposeEffect?: () => void;

  static styles = css`
    :host {
      display: inline-block;
    }

    .count {
      font-weight: inherit;
      font-size: inherit;
    }

    .loading {
      opacity: 0.6;
    }
  `;

  connectedCallback() {
    super.connectedCallback();

    // Set up reactive effect that runs whenever the signal changes
    this._disposeEffect = effect(() => {
      this.count = itemCount.value;
      this.loading = isCartLoading.value;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up the effect when component is removed
    this._disposeEffect?.();
  }

  render() {
    const displayCount = this.count >= 100 ? '99+' : this.count;

    return html`
      <span class="count ${this.loading ? 'loading' : ''}">
        ${displayCount}
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cart-count': CartCount;
  }
}
